import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import API_BASE from "./apiBase";
import { displayName, useMaskNames } from "./nameMask";
import { fmtEta } from "./etaFormat";

/**
 * ===========================================================================
 * FLEET MAP - where every driver is right now
 * ===========================================================================
 * One screen, one question: who is out, where are they, and what have they
 * still got aboard.
 *
 * ONE CALL, NOT ONE PER PIN. GET /api/headoffice/map/live returns each driver
 * WITH their remaining drops nested. A fleet is tens of drivers, so the
 * payload is small, and the alternative - fetching the drop list when a pin is
 * clicked - puts a spinner in front of the user at the exact moment they asked
 * a question.
 *
 * REFRESHES ON ITS OWN, EVERY TWO MINUTES. That is the interval the driver app
 * posts breadcrumbs at, so polling faster only redraws the same pins. The
 * manual Refresh button stays because the automatic one is invisible: someone
 * watching a driver they are worried about needs to be able to make something
 * happen.
 *
 * THE POLL DOES NOT BLANK THE MAP. Only the first load shows a loading state;
 * later ones swap the data underneath. A map that flashes empty every two
 * minutes is unusable, and a failed poll leaves the last known positions on
 * screen with the error beside them rather than throwing them away - stale
 * positions are worth a great deal more than none.
 *
 * A DRIVER WITH NO POSITION STILL GETS A PIN. The server falls back to their
 * last completed delivery, then to the branch, and says which it used. The pin
 * is drawn hollow and the popup says the position is not current, because
 * "we have lost contact with Dave" is the most important thing this screen
 * ever has to say and hiding his pin would say nothing at all.
 *
 * LIVES IN THE RIGHT-HAND COLUMN of the dashboard, always on, rather than
 * behind a button. A map you have to ask for is a map nobody looks at.
 * "Move to new screen" opens the same map full screen in its own browser tab
 * (?fleetmap=1, handled in App.js) rather than an in-page overlay, because
 * half a screen is tight for reading a spread-out fleet and a separate tab
 * can be dragged to another monitor and left running.
 *
 * RESIZES ITSELF. Leaflet measures its container once and paints grey where it
 * thinks there is nothing. Toggling a dashboard row changes that container's
 * height underneath it, so the size is watched and the map told to remeasure.
 */

const POLL_MS = 2 * 60 * 1000;

/** Fallback view when nobody has a position yet: roughly the middle of the UK. */
const DEFAULT_CENTRE = [54.0, -2.5];
const DEFAULT_ZOOM = 6;

const S = {
  // The dashboard column. Matches the bordered row blocks beside it so the
  // two halves read as one page rather than a page with a map stuck on it.
  panel: {
    display: "flex", flexDirection: "column",
    border: "1px solid #d1d5db", borderRadius: 10, background: "#fcfcfd",
    overflow: "hidden", height: "100%", minHeight: 0,
    fontFamily: "system-ui, sans-serif", color: "#111827",
  },
  full: {
    position: "fixed", inset: 0, zIndex: 4000, background: "#fff",
    display: "flex", flexDirection: "column", borderRadius: 0, border: "none",
    fontFamily: "system-ui, sans-serif", color: "#111827",
  },
  bar: {
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
    padding: "10px 14px", borderBottom: "1px solid #e5e7eb", background: "#fcfcfd",
  },
  title: { fontSize: 15, fontWeight: 800, margin: 0 },
  muted: { fontSize: 12, color: "#6b7280" },
  btn: {
    background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db",
    borderRadius: 4, padding: "6px 12px", fontSize: 13, cursor: "pointer",
  },
  btnGo: {
    background: "#0f766e", color: "#fff", border: "1px solid #0f766e",
    borderRadius: 4, padding: "6px 12px", fontSize: 13, cursor: "pointer",
  },
  error: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
    padding: "4px 10px", borderRadius: 4, fontSize: 12,
  },
};

/** HH:MM in the reader's own timezone. */
function fmtClock(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime())
    ? null
    : t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * A pin that carries its own label.
 *
 * A plain marker plus a tooltip would hide the name until hover, and the whole
 * point of this screen is reading the fleet at a glance. So the name and the
 * outstanding count are drawn INTO the icon.
 *
 * Names are escaped by hand because divIcon takes an HTML string, not JSX. A
 * driver called O'Brien would otherwise break the markup, and any name at all
 * reaching innerHTML unescaped is an injection route.
 */
function escapeHtml(value) {
  return String(value === null || value === undefined ? "" : value)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function driverIcon(driver, name) {
  const live = driver.positionSource === "driver";
  const stale = live && driver.fixAgeMinutes !== null && driver.fixAgeMinutes > 15;

  // Green while reporting, amber when the fix has gone quiet, grey when the
  // position is a fallback rather than a report.
  const colour = !live ? "#6b7280" : stale ? "#b45309" : "#0f766e";
  const fill = !live ? "#f3f4f6" : stale ? "#fef3c7" : "#ccfbf1";

  const drops = driver.dropsLeft;
  const label = `${escapeHtml(name)}`;
  const sub = `${drops} drop${drops === 1 ? "" : "s"} left`;

  return L.divIcon({
    className: "fleet-pin",
    html: `
      <div style="
        transform: translate(-50%, -100%);
        display: inline-flex; flex-direction: column; align-items: center;
        white-space: nowrap; pointer-events: auto;">
        <div style="
          background:${fill}; border:2px solid ${colour}; color:#111827;
          border-radius:6px; padding:3px 7px; font-size:11px; font-weight:700;
          font-family: system-ui, sans-serif; line-height:1.25;
          box-shadow:0 1px 3px rgba(0,0,0,0.3); text-align:center;">
          <div>${label}</div>
          <div style="font-weight:600; color:${colour};">${sub}</div>
        </div>
        <div style="
          width:0; height:0; margin-top:-1px;
          border-left:5px solid transparent; border-right:5px solid transparent;
          border-top:7px solid ${colour};"></div>
      </div>`,
    // The icon anchors itself with the transform above, so leaflet is told the
    // point is the origin. Sizing it here as well would double the offset.
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

/**
 * Fits the view to the pins ONCE, on the first load that has any.
 *
 * Deliberately not on every poll: refitting every two minutes would yank the
 * map out from under someone who has zoomed into a particular driver, which is
 * the main thing anybody does on this screen.
 */
function FitToDrivers({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    done.current = true;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [60, 60] });
    }
  }, [points, map]);
  return null;
}

/**
 * Keeps leaflet's idea of its own size honest.
 *
 * The map sizes itself when it mounts. In a dashboard column that container
 * changes height whenever a row is shown or hidden, and going full screen
 * changes both dimensions at once - after which leaflet keeps painting the old
 * viewport and fills the difference with grey. invalidateSize on every resize
 * is the documented fix.
 */
function KeepSized() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    // A frame's delay: the element has to have been laid out at its new size
    // before leaflet measures it, or it reads the value it already had.
    const remeasure = () => requestAnimationFrame(() => map.invalidateSize());
    remeasure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(remeasure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

/** The ordered drop list inside a pin's popup. */
function DropList({ drops, masked }) {
  if (!drops.length) {
    return <div style={S.muted}>Nothing left aboard.</div>;
  }
  return (
    <div style={{ maxHeight: 260, overflowY: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
        <tbody>
          {drops.map((d, i) => {
            const eta = fmtEta(d);
            return (
              <tr key={d.recipientId}
                  style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "4px 6px 4px 0", verticalAlign: "top",
                             color: "#6b7280", fontWeight: 700, width: 22 }}>
                  {d.dropsAway === null ? "-" : i + 1}
                </td>
                <td style={{ padding: "4px 0", verticalAlign: "top" }}>
                  <div style={{ fontWeight: 600 }}>
                    {displayName(d.recipientName, masked)}
                    {d.parcels > 1 && (
                      <span style={{ color: "#6b7280", fontWeight: 400 }}>
                        {" "}({d.parcels} parcels)
                      </span>
                    )}
                  </div>
                  {(d.address || d.postcode) && (
                    <div style={{ color: "#6b7280", fontSize: 11 }}>
                      {[d.address, d.postcode].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {d.dropsAway === null ? (
                    <div style={{ color: "#6b7280", fontSize: 11 }}>
                      Not in the route order yet
                    </div>
                  ) : eta ? (
                    <div style={{ color: "#6b7280", fontSize: 11 }}>{eta}</div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function FleetMap({ token, standalone = false }) {
  const masked = useMaskNames();

  const [drivers, setDrivers] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(true);   // first load only
  const [busy, setBusy] = useState(false);        // any load, including polls
  const [error, setError] = useState(null);

  // Guards a slow response from a previous poll landing after a newer one and
  // putting stale pins back on the map.
  const seq = useRef(0);
  const alive = useRef(true);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/map/live`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Map data failed (${res.status})`);
      const data = await res.json();
      if (!alive.current || mine !== seq.current) return;
      setDrivers(Array.isArray(data.drivers) ? data.drivers : []);
      setGeneratedAt(data.generatedAt || null);
      setError(null);
    } catch (e) {
      if (!alive.current || mine !== seq.current) return;
      // The pins already on screen are left alone. A poll that fails should
      // cost you the freshness, not the information.
      setError(e.message);
    } finally {
      if (alive.current && mine === seq.current) {
        setBusy(false);
        setLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    alive.current = true;
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      alive.current = false;
      clearInterval(timer);
    };
  }, [load]);

  const placed = useMemo(
    () => drivers.filter(d => Number.isFinite(Number(d.lat)) && Number.isFinite(Number(d.lng))),
    [drivers],
  );
  const points = useMemo(
    () => placed.map(d => [Number(d.lat), Number(d.lng)]),
    [placed],
  );

  const dropsTotal = drivers.reduce((n, d) => n + (d.dropsLeft || 0), 0);
  const noFix = drivers.filter(d => d.positionSource !== "driver").length;

  // Opens the same map, full screen, in a brand new tab — rather than an
  // in-page overlay — so it can be dragged to another monitor and left
  // running while the dashboard it came from stays open where it was.
  // localStorage (and so the token) is shared across tabs on the same
  // origin, so the new tab opens already signed in.
  const openInNewTab = () => {
    window.open(`${window.location.pathname}?fleetmap=1`, "_blank", "noopener");
  };

  return (
    <div style={standalone ? S.full : S.panel}>
      <div style={S.bar}>
        <h2 style={S.title}>Fleet map</h2>
        <span style={S.muted}>
          {loading ? "Loading…"
            : `${drivers.length} driver${drivers.length === 1 ? "" : "s"} out · ${dropsTotal} drop${dropsTotal === 1 ? "" : "s"} left`}
          {noFix > 0 && !loading && ` · ${noFix} not reporting a position`}
        </span>

        <div style={{ flex: 1 }} />

        {error && <span style={S.error}>{error}</span>}
        {generatedAt && (
          <span style={S.muted} title="Refreshes on its own every two minutes.">
            Updated {fmtClock(generatedAt)}
          </span>
        )}
        <button style={S.btn} onClick={load} disabled={busy}>
          {busy ? "Refreshing…" : "Refresh"}
        </button>
        {/* Hidden once already standalone — opening a new tab from the new
            tab would just be another copy of the same screen. */}
        {!standalone && (
          <button style={S.btnGo} onClick={openInNewTab}
            title="Opens the fleet map, full screen, in a new browser tab.">
            Move to new screen
          </button>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {!loading && drivers.length === 0 && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 500, display: "flex",
            alignItems: "center", justifyContent: "center", pointerEvents: "none",
          }}>
            <div style={{
              background: "rgba(255,255,255,0.92)", border: "1px solid #d1d5db",
              borderRadius: 8, padding: "12px 18px", fontSize: 13, color: "#374151",
            }}>
              Nobody is out at the moment.
            </div>
          </div>
        )}

        <MapContainer
          center={points.length ? points[0] : DEFAULT_CENTRE}
          zoom={points.length ? 12 : DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <KeepSized />
          <FitToDrivers points={points} />

          {placed.map((d) => {
            const name = displayName(d.driverName, masked);
            return (
              <Marker
                key={d.roundId}
                position={[Number(d.lat), Number(d.lng)]}
                icon={driverIcon(d, name)}
              >
                <Popup maxWidth={380} minWidth={280}>
                  <div style={{ fontFamily: "system-ui, sans-serif" }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{name}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
                      {displayName(d.branchName, masked)}
                      {" · "}
                      {d.dropsLeft} drop{d.dropsLeft === 1 ? "" : "s"} left
                      {d.parcelsLeft !== d.dropsLeft && ` · ${d.parcelsLeft} parcels`}
                    </div>

                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
                      {d.positionSource === "driver" && (
                        d.fixAgeMinutes === null
                          ? "Position reported by the driver."
                          : d.fixAgeMinutes < 1
                            ? "Position reported just now."
                            : `Position reported ${d.fixAgeMinutes} min ago.`
                      )}
                      {d.positionSource === "last_drop"
                        && "No recent position. Shown at their last completed delivery."}
                      {d.positionSource === "branch"
                        && "No position reported. Shown at the branch."}
                    </div>

                    <div style={{
                      fontSize: 10.5, fontWeight: 800, color: "#6b7280",
                      textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4,
                    }}>
                      Remaining drops, in order
                    </div>
                    <DropList drops={d.drops || []} masked={masked} />
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
